import React, { FC, useEffect, useState } from "react";

import { useGetObjectDatasets } from "api/api-hooks/object-dataset-hooks";
import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import { DashboardEditorProps } from "components/ComponentTypeTypes";
import useCurrentCompanyContext from "hooks/useCurrentCompanyContext";
import { AutoComplete, AutoCompleteChangeEvent, AutoCompleteCompleteEvent } from "primereact/autocomplete";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { T } from "services/lang/LanguageService";
import { InputNumber } from "primereact/inputnumber";
import LoadConfigInputs from "../charts/LoadConfigInputs";
import { ObjectDatasetConfig } from "./ObjectDatasetConfig";
import { DatasetLoadingConf } from "../charts/interfaces";

const DefaultLoadingConf: DatasetLoadingConf = {
  lead: 0,
  lag: 1,
  lagUnit: "MONTHS",
  leadUnit: "MONTHS",
  alignUnit: "MINUTES",
};

const ObjectDatasetEditor: FC<DashboardEditorProps<ObjectDatasetConfig>> = ({ config, onConfigChange }) => {
  const [items, setItems] = useState<string[]>([]);
  const companyRef = useCurrentCompanyContext()?.companyReference ?? "";
  const { data, isLoading } = useGetObjectDatasets(companyRef, {
    enabled: !!companyRef,
  });

  useEffect(() => {
    if (data) {
      setItems([
        ...new Set(
          data
            .find(d => d.identifier === config.identifier)
            ?.data.flatMap(d => d?.fileInfos.map(f => f.key)) ?? []
        ),
      ]);
    }
  }, [config.identifier, data]);

  const selectedDataset = data?.find(d => d.identifier === config.identifier);

  const onChange = (e: AutoCompleteChangeEvent | DropdownChangeEvent) => {
    const { value, name } = e.target;
    onConfigChange({ ...config, [name]: value });
  };

  const search = (e: AutoCompleteCompleteEvent) => {
    let files;

    if (!e.query.trim().length) {
      files = [...items];
    } else {
      files = items.filter(item => {
        return item.toLowerCase().includes(e.query.toLowerCase());
      });
    }
    setItems(files);
  };

  return (
    <div className="flex gap-3 flex-wrap">
      <InputLabelWrapper labelName={T("label.object-dataset")} col={4}>
        <Dropdown
          options={data ?? []}
          onChange={onChange}
          value={config.identifier}
          optionValue="identifier"
          optionLabel="title"
          name="identifier"
          placeholder={T("select.model")}
          loading={isLoading}
        />
      </InputLabelWrapper>
      {selectedDataset?.type === "HTML" && (
        <InputLabelWrapper labelName={T("word.file")} col={4}>
          <AutoComplete
            suggestions={items}
            completeMethod={search}
            onChange={onChange}
            name="initialFileKey"
            inputClassName="w-full"
            value={config.initialFileKey}
            placeholder={T("placeholder.write-initial-file-name")}
          />
        </InputLabelWrapper>
      )}
      {selectedDataset?.type === "GENERAL" && (
        <>
          <InputLabelWrapper labelName={T("tabs-display.rows")} col={1}>
            <InputNumber
              value={config.rows}
              onChange={e => onConfigChange({ ...config, rows: e.value })}
              placeholder="10"
              min={0}
              useGrouping={false}
            />
          </InputLabelWrapper>
          <InputLabelWrapper labelName={T("action.use-load-config")} col={2}>
            <InputSwitch
              checked={!!config.loadConf}
              onChange={e =>
                onConfigChange({ ...config, loadConf: e.value ? DefaultLoadingConf : undefined })
              }
            />
          </InputLabelWrapper>
          <InputLabelWrapper labelName={T("action.show-delete-button")} col={2}>
            <InputSwitch
              checked={!!config.showDeleteButton}
              onChange={e => onConfigChange({ ...config, showDeleteButton: e.value })}
            />
          </InputLabelWrapper>
          {config.loadConf && (
            <div className="flex flex-wrap">
              <LoadConfigInputs
                loadConf={config.loadConf ?? DefaultLoadingConf}
                handleLoadConfChange={conf => onConfigChange({ ...config, loadConf: conf })}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ObjectDatasetEditor;
